<template>
  <!-- The rows of "warnings on route", in the order the server sent them
       (danger first, then by distance along the route): hazards with their
       sprite icon and Blocked / Passable, difficult sections with the terrain
       triangle, their grade (T3-T6) and where they run. One row per card (a
       hazard type / SAC grade): its first alert, and "+N more"
       when the route has more of the kind. A hazard row is a
       button (it opens the hazard). A section row is one only where it does
       something (the planner shows it on the map); elsewhere (the hike page)
       it is plain text: no chevron, no hover, not focusable. -->
  <ul class="route-alert-list" data-test="route-alert-list">
    <li v-for="row in rows" :key="row.id" class="route-alert-list__item">
      <component
        :is="isStatic(row) ? 'div' : 'button'"
        :type="isStatic(row) ? null : 'button'"
        class="route-alert-row"
        :class="[`route-alert-row--${row.level}`, { 'route-alert-row--static': isStatic(row) }]"
        data-test="route-alert-row"
        :data-alert-id="row.id"
        :data-kind="row.kind"
        v-on="isStatic(row) ? {} : { click: () => $emit('select', row) }"
      >
        <TerrainSectionIcon v-if="row.terrainColour" :colour="row.terrainColour" :size="36" class="route-alert-row__icon" />
        <HazardTypeIcon v-else :name="row.iconName" :size="36" class="route-alert-row__icon" />
        <span class="route-alert-row__body">
          <span class="route-alert-row__title" data-test="route-alert-title">{{ row.title }}</span>
          <span class="route-alert-row__meta">
            <span
              v-if="row.passabilityTag"
              class="route-alert-tag"
              :class="`route-alert-tag--${row.passabilityTag.kind}`"
              data-test="route-alert-passability"
            >{{ row.passabilityTag.text }}</span>
            <span
              v-if="row.gradeTag"
              class="route-alert-tag"
              :class="row.gradeTag.level === 'danger' ? 'route-alert-tag--blocked' : 'route-alert-tag--passable'"
              data-test="route-alert-grade"
            >{{ row.gradeTag.text }}</span>
            <span v-if="row.distanceText" class="route-alert-row__distance" data-test="route-alert-distance">{{ row.distanceText }}</span>
          </span>
          <span v-if="row.moreText" class="route-alert-row__more" data-test="route-alert-more">{{ row.moreText }}</span>
        </span>
        <span v-if="row.selectable && !isStatic(row)" class="material-icons route-alert-row__chevron" aria-hidden="true">chevron_right</span>
      </component>
    </li>
  </ul>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import HazardTypeIcon from '../../trail_hazards/components/HazardTypeIcon.vue';
import TerrainSectionIcon from './TerrainSectionIcon.vue';
import type { RouteAlertRowView } from '../../../utils/routeAlerts/routeAlertsModel';

export default defineComponent({
  name: 'RouteAlertList',
  components: { HazardTypeIcon, TerrainSectionIcon },
  props: {
    rows: { type: Array as PropType<RouteAlertRowView[]>, required: true },
    /** Whether a difficult section's row can be picked (the planner); plain text when not. */
    interactiveSections: { type: Boolean, default: true },
  },
  emits: ['select'],
  methods: {
    isStatic(row: RouteAlertRowView): boolean {
      return !!row.sectionId && !this.interactiveSections;
    },
  },
});
</script>

<style scoped>
.route-alert-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.route-alert-list__item + .route-alert-list__item {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.route-alert-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.7rem 0.25rem;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

button.route-alert-row:hover,
button.route-alert-row:focus-visible {
  background: rgba(255, 255, 255, 0.05);
}

button.route-alert-row:focus-visible {
  outline: 2px solid #f5dd42;
  outline-offset: -2px;
}

.route-alert-row--static {
  cursor: default;
}

.route-alert-row__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.route-alert-row__title {
  color: #fff;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.route-alert-row__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
  font-size: 0.78rem;
}

.route-alert-tag {
  padding: 0.12rem 0.45rem;
  border-radius: 5px;
  font-weight: 700;
  white-space: nowrap;
}

.route-alert-tag--passable {
  color: #ffa34d;
  background: rgba(245, 119, 0, 0.18);
}

.route-alert-tag--blocked {
  color: #ff8a90;
  background: rgba(219, 43, 53, 0.18);
}

.route-alert-row__more {
  color: #b2b5c0;
  font-size: 0.78rem;
}

.route-alert-row__distance {
  color: #b2b5c0;
  white-space: nowrap;
}

.route-alert-row__chevron {
  flex: 0 0 auto;
  color: #9da0ad;
}
</style>
